"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronUp,
  MessageSquare,
  Plus,
  Loader2,
  Sparkles,
  Bug,
  Lightbulb,
  Rocket,
  Clock,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { telemetry } from "@/lib/telemetry";
import {
  apiClient,
  ApiClientError,
  type FeedbackPost,
  type FeedbackPostDetail,
  type FeedbackPostType,
  type FeedbackPostStatus,
} from "@/lib/api-client";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

type SortKey = "top" | "new";
type TypeFilter = "all" | FeedbackPostType;

const TYPE_META: Record<
  FeedbackPostType,
  { label: string; className: string; Icon: LucideIcon }
> = {
  feature: {
    label: "Feature",
    className: "text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.08]",
    Icon: Sparkles,
  },
  bug: {
    label: "Bug",
    className: "text-rose-300 border-rose-400/30 bg-rose-400/[0.08]",
    Icon: Bug,
  },
  idea: {
    label: "Idea",
    className: "text-sky-300 border-sky-400/30 bg-sky-400/[0.08]",
    Icon: Lightbulb,
  },
};

const STATUS_META: Record<
  FeedbackPostStatus,
  { label: string; className: string; Icon?: LucideIcon }
> = {
  open: {
    label: "Open",
    className: "text-white/90 border-white/15 bg-transparent",
  },
  planned: {
    label: "Planned",
    className: "text-sky-300 border-sky-400/30 bg-sky-400/[0.08]",
    Icon: Clock,
  },
  shipped: {
    label: "Shipped",
    className: "text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.08]",
    Icon: Rocket,
  },
  resolved: {
    label: "Resolved",
    className: "text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.08]",
    Icon: CheckCircle2,
  },
};

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "feature", label: "Features" },
  { key: "bug", label: "Bugs" },
  { key: "idea", label: "Ideas" },
];

const NEW_POST_TYPES: { key: FeedbackPostType; label: string }[] = [
  { key: "feature", label: "Feature" },
  { key: "bug", label: "Bug" },
  { key: "idea", label: "Idea" },
];

function timeAgo(iso: string): string {
  try {
    const d = new Date(iso);
    if (d > new Date()) return "just now";
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return "";
  }
}

function initials(name: string): string {
  const letters = name.replace(/[^a-zA-Z]/g, "");
  return (letters.slice(0, 1) || "X").toUpperCase();
}

function feedbackErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) {
    const detail = error.data?.detail;
    if (typeof detail === "string" && detail) return detail;
    if (detail?.message) return detail.message;
  }
  return fallback;
}

/* ── small shared bits ─────────────────────────────────────────── */

function TypeBadge({ type }: { type: FeedbackPostType }) {
  const meta = TYPE_META[type];
  const Icon = meta.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium",
        meta.className,
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {meta.label}
    </span>
  );
}

function StatusBadge({ status }: { status: FeedbackPostStatus }) {
  const meta = STATUS_META[status];
  const Icon = meta.Icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium",
        meta.className,
      )}
    >
      {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
      {meta.label}
    </span>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-400/15 text-[10px] font-semibold text-emerald-200 ring-1 ring-emerald-400/20"
    >
      {initials(name)}
    </span>
  );
}

function UpvoteButton({
  upvotes,
  hasVoted,
  onClick,
  label,
}: {
  upvotes: number;
  hasVoted: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      aria-pressed={hasVoted}
      aria-label={`Upvote ${label}`}
      className={cn(
        "group inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60",
        hasVoted
          ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
          : "border-white/10 bg-white/[0.03] text-white/90 hover:border-emerald-400/40 hover:text-white",
      )}
    >
      <ChevronUp className="h-4 w-4" aria-hidden />
      <span>Upvote</span>
      <span
        className={cn(
          "ml-0.5 tabular-nums",
          hasVoted ? "text-emerald-300/80" : "text-white/50",
        )}
      >
        {upvotes}
      </span>
    </button>
  );
}

/* ── main board ────────────────────────────────────────────────── */

export function FeedbackBoard() {
  const { user } = useAuth();
  const pageViewSentRef = useRef(false);

  const [posts, setPosts] = useState<FeedbackPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sort, setSort] = useState<SortKey>("new");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");

  const [newPostOpen, setNewPostOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const [activePost, setActivePost] = useState<FeedbackPostDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await apiClient.listFeedbackPosts({ sort, limit: 100 });
      setPosts(res.posts);
      telemetry.emit("feedback.list_loaded", {
        user_id: user?.uid,
        sort,
        total: res.total,
      });
    } catch (e) {
      console.error("[feedback] failed to load posts", e);
      telemetry.emit("feedback.list_error", {
        user_id: user?.uid,
        sort,
        error_message: e instanceof Error ? e.message : String(e),
      });
      setPosts([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [sort, user?.uid]);

  useEffect(() => {
    if (!pageViewSentRef.current) {
      pageViewSentRef.current = true;
      telemetry.emit("feedback.page_view", {
        user_id: user?.uid,
      });
    }
    loadPosts();
  }, [loadPosts, user?.uid]);

  const visiblePosts = useMemo(() => {
    if (typeFilter === "all") return posts;
    return posts.filter((p) => p.type === typeFilter);
  }, [posts, typeFilter]);

  const requireLogin = useCallback(() => {
    if (user) return true;
    setLoginOpen(true);
    return false;
  }, [user]);

  // Optimistic vote toggle that keeps the list and the open detail in sync.
  const handleVote = useCallback(
    async (postId: string) => {
      if (!requireLogin()) return;
      telemetry.emit("feedback.vote_click", {
        user_id: user?.uid,
        post_id: postId,
      });

      const apply = (p: FeedbackPost): FeedbackPost => {
        const nextVoted = !p.has_voted;
        return {
          ...p,
          has_voted: nextVoted,
          upvotes: Math.max(0, p.upvotes + (nextVoted ? 1 : -1)),
        };
      };
      setPosts((prev) => prev.map((p) => (p.id === postId ? apply(p) : p)));
      setActivePost((prev) =>
        prev && prev.id === postId ? { ...prev, ...apply(prev) } : prev,
      );

      try {
        const res = await apiClient.toggleFeedbackVote(postId);
        telemetry.emit("feedback.vote_success", {
          user_id: user?.uid,
          post_id: postId,
          has_voted: res.has_voted,
          upvotes: res.upvotes,
        });
        const sync = <T extends FeedbackPost>(p: T): T => ({
          ...p,
          upvotes: res.upvotes,
          has_voted: res.has_voted,
        });
        setPosts((prev) => prev.map((p) => (p.id === postId ? sync(p) : p)));
        setActivePost((prev) =>
          prev && prev.id === postId ? sync(prev) : prev,
        );
      } catch (e) {
        // Revert on failure (e.g. expired token) and nudge to log in.
        console.error("[feedback] vote toggle failed", e);
        telemetry.emit("feedback.vote_error", {
          user_id: user?.uid,
          post_id: postId,
          error_message: e instanceof Error ? e.message : String(e),
        });
        loadPosts();
        if (!user) setLoginOpen(true);
      }
    },
    [requireLogin, loadPosts, user],
  );

  const openDetail = useCallback(async (postId: string) => {
    telemetry.emit("feedback.detail_open", {
      user_id: user?.uid,
      post_id: postId,
    });
    setDetailOpen(true);
    setDetailLoading(true);
    setActivePost(null);
    try {
      const detail = await apiClient.getFeedbackPost(postId);
      setActivePost(detail);
    } catch (e) {
      console.error("[feedback] failed to load post detail", e);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, [user?.uid]);

  const handlePostCreated = useCallback((post: FeedbackPost) => {
    setPosts((prev) => [post, ...prev]);
    setNewPostOpen(false);
  }, []);

  const handleCommentAdded = useCallback(() => {
    setActivePost((prev) =>
      prev ? { ...prev, comment_count: prev.comment_count + 1 } : prev,
    );
    setPosts((prev) =>
      prev.map((p) =>
        activePost && p.id === activePost.id
          ? { ...p, comment_count: p.comment_count + 1 }
          : p,
      ),
    );
  }, [activePost]);

  return (
    <div className="w-full max-w-[1180px] mx-auto px-6 sm:px-14 pt-6 pb-28">
      {/* Hero: identical pattern to the Library page hero */}
      <div className="mb-8">
        <div className="mb-3 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400">
            The Feedback Board
          </span>
        </div>
        <h1 className="text-3xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl">
          Tell us what to build next.
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
          Upvote the ideas you want, post your own, and weigh in on what others
          are asking for.
        </p>
      </div>

      {/* Controls */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {/* Sort */}
          <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
            {(["top", "new"] as SortKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setSort(key)}
                aria-pressed={sort === key}
                className={cn(
                  "rounded-md px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60",
                  sort === key
                    ? "bg-white text-black"
                    : "text-white/90 hover:text-white",
                )}
              >
                {key === "top" ? "Top" : "New"}
              </button>
            ))}
          </div>

          {/* Type filter */}
          <div className="hidden items-center gap-1.5 sm:flex">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setTypeFilter(f.key)}
                aria-pressed={typeFilter === f.key}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60",
                  typeFilter === f.key
                    ? "border-white/15 bg-white/[0.08] text-white"
                    : "border-white/10 bg-transparent text-white/80 hover:text-white hover:border-white/20",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            if (requireLogin()) {
              telemetry.emit("feedback.create_open", {
                user_id: user?.uid,
              });
              setNewPostOpen(true);
            }
          }}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white px-3.5 text-sm font-medium text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
        >
          <Plus className="h-4 w-4" aria-hidden />
          Post feedback
        </button>
      </div>

      {/* List */}
      {loading ? (
        <ul className="space-y-2.5" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <li
              key={i}
              className="h-[88px] animate-pulse rounded-lg border border-white/[0.06] bg-white/[0.02]"
            />
          ))}
        </ul>
      ) : loadError ? (
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.04] px-6 py-16 text-center">
          <p className="text-[15px] font-medium text-white">
            Could not load feedback.
          </p>
          <p className="mt-1 text-sm text-white/90">
            Something went wrong reaching the server. Please try again.
          </p>
          <button
            type="button"
            onClick={() => loadPosts()}
            className="mt-4 inline-flex h-9 items-center rounded-md border border-white/15 bg-white/[0.04] px-3.5 text-sm font-medium text-white transition-colors hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
          >
            Try again
          </button>
        </div>
      ) : visiblePosts.length === 0 ? (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-6 py-16 text-center">
          <p className="text-[15px] font-medium text-white">
            No feedback here yet.
          </p>
          <p className="mt-1 text-sm text-white/90">
            Be the first to post what you want us to build.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {visiblePosts.map((post) => (
            <li key={post.id}>
              <article className="group flex flex-col gap-3 rounded-xl border border-white/10 bg-transparent p-5 transition-colors hover:border-white/20">
                <div className="flex items-start justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => openDetail(post.id)}
                    aria-haspopup="dialog"
                    className="flex-1 min-w-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 rounded-md"
                  >
                    <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                      <h3 className="text-base font-semibold text-white truncate">
                        {post.title}
                      </h3>
                      <TypeBadge type={post.type} />
                      {post.status !== "open" && (
                        <StatusBadge status={post.status} />
                      )}
                    </div>
                    {post.body && (
                      <p className="line-clamp-2 text-sm leading-relaxed text-white/90">
                        {post.body}
                      </p>
                    )}
                  </button>
                  <div className="shrink-0">
                    <UpvoteButton
                      upvotes={post.upvotes}
                      hasVoted={post.has_voted}
                      onClick={() => handleVote(post.id)}
                      label={post.title}
                    />
                  </div>
                </div>
                <div className="mt-1 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => openDetail(post.id)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 -ml-3 text-[13px] font-medium text-white/90 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60"
                    >
                      <MessageSquare className="h-4 w-4" aria-hidden />
                      <span>
                        {post.comment_count}{" "}
                        {post.comment_count === 1 ? "answer" : "answers"}
                      </span>
                    </button>
                  </div>
                  <div className="flex items-center gap-2 text-[13px] text-white/90">
                    <Avatar name={post.author.name} />
                    <span className="hidden sm:inline-block">
                      {post.author.name}
                    </span>
                    <span className="hidden sm:inline-block" aria-hidden>
                      ·
                    </span>
                    <span>{timeAgo(post.created_at)}</span>
                  </div>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}

      {/* New post dialog */}
      <NewPostDialog
        open={newPostOpen}
        onOpenChange={setNewPostOpen}
        onCreated={handlePostCreated}
      />

      {/* Post detail dialog */}
      <PostDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        post={activePost}
        loading={detailLoading}
        isLoggedIn={Boolean(user)}
        onVote={handleVote}
        onRequireLogin={() => setLoginOpen(true)}
        onCommentAdded={handleCommentAdded}
        setActivePost={setActivePost}
      />

      {/* Login prompt */}
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
}

/* ── new post dialog ───────────────────────────────────────────── */

function NewPostDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (post: FeedbackPost) => void;
}) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState<FeedbackPostType>("feature");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle("");
    setBody("");
    setType("feature");
    setError(null);
  };

  const submit = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      setError("Give your feedback a short title.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const post = await apiClient.createFeedbackPost({
        title: trimmed,
        body: body.trim(),
        type,
      });
      telemetry.emit("feedback.create_success", {
        user_id: user?.uid,
        post_id: post.id,
        type,
      });
      reset();
      onCreated(post);
    } catch (e) {
      console.error("[feedback] create post failed", e);
      telemetry.emit("feedback.create_error", {
        user_id: user?.uid,
        type,
        error_message: e instanceof Error ? e.message : String(e),
      });
      setError(feedbackErrorMessage(e, "Could not post that. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg border-white/10 bg-[#0c0c0e] p-6 text-white sm:rounded-xl">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-bold text-white">Post feedback</DialogTitle>
          <DialogDescription className="text-[14px] text-white/90">
            Share an idea, request a feature, or report a bug. Keep it short and
            specific.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="flex items-center gap-2">
            {NEW_POST_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setType(t.key)}
                aria-pressed={type === t.key}
                className={cn(
                  "rounded-md border px-3 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60",
                  type === t.key
                    ? "border-white/15 bg-white/[0.08] text-white"
                    : "border-white/10 text-white/80 hover:border-white/20 hover:text-white",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div>
            <label htmlFor="fb-title" className="sr-only">
              Title
            </label>
            <input
              id="fb-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
              placeholder="Short, specific title"
              className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-2.5 text-[14px] text-white placeholder:text-white/40 transition-colors focus:border-emerald-400/50 focus:bg-white/[0.04] focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
            />
          </div>

          <div>
            <label htmlFor="fb-body" className="sr-only">
              Details
            </label>
            <textarea
              id="fb-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={4000}
              rows={4}
              placeholder="Add any details that help us understand it (optional)"
              className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-3 text-[14px] leading-relaxed text-white placeholder:text-white/40 transition-colors focus:border-emerald-400/50 focus:bg-white/[0.04] focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
            />
          </div>

          {error && <p className="text-[13px] text-rose-300">{error}</p>}
        </div>

        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 rounded-md px-4 text-[13px] font-medium text-white/90 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-white px-4 text-[13px] font-medium text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            Post
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── post detail dialog ────────────────────────────────────────── */

function PostDetailDialog({
  open,
  onOpenChange,
  post,
  loading,
  isLoggedIn,
  onVote,
  onRequireLogin,
  onCommentAdded,
  setActivePost,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  post: FeedbackPostDetail | null;
  loading: boolean;
  isLoggedIn: boolean;
  onVote: (postId: string) => void;
  onRequireLogin: () => void;
  onCommentAdded: () => void;
  setActivePost: React.Dispatch<
    React.SetStateAction<FeedbackPostDetail | null>
  >;
}) {
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setComment("");
      setError(null);
    }
  }, [open]);

  const submitComment = async () => {
    if (!post) return;
    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }
    const trimmed = comment.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiClient.addFeedbackComment(post.id, trimmed);
      telemetry.emit("feedback.answer_success", {
        user_id: user?.uid,
        post_id: post.id,
        comment_id: created.id,
      });
      setActivePost((prev) =>
        prev && prev.id === post.id
          ? { ...prev, comments: [...prev.comments, created] }
          : prev,
      );
      setComment("");
      onCommentAdded();
    } catch (e) {
      console.error("[feedback] add comment failed", e);
      telemetry.emit("feedback.answer_error", {
        user_id: user?.uid,
        post_id: post.id,
        error_message: e instanceof Error ? e.message : String(e),
      });
      setError(
        feedbackErrorMessage(e, "Could not add your answer. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0c0c0e] p-6 text-white sm:rounded-xl">
        {loading || !post ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-5 w-5 animate-spin text-white/80" aria-hidden />
          </div>
        ) : (
          <>
            <DialogHeader className="text-left">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <DialogTitle className="text-xl font-bold text-white sm:text-2xl">
                  {post.title}
                </DialogTitle>
                <TypeBadge type={post.type} />
                {post.status !== "open" && <StatusBadge status={post.status} />}
              </div>
              <DialogDescription className="sr-only">
                Feedback post detail and comments
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 flex flex-col gap-5">
              <div className="flex items-center gap-2.5 text-[13px] text-white/90">
                <Avatar name={post.author.name} />
                <span className="font-medium text-white/90">
                  {post.author.name}
                </span>
                <span aria-hidden>·</span>
                <span>{timeAgo(post.created_at)}</span>
              </div>

              {post.body && (
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-white/90">
                  {post.body}
                </p>
              )}

              <div className="flex items-center gap-3 pt-1">
                <UpvoteButton
                  upvotes={post.upvotes}
                  hasVoted={post.has_voted}
                  onClick={() => onVote(post.id)}
                  label={post.title}
                />
              </div>
            </div>

            {/* Comments */}
            <div className="mt-8 border-t border-white/[0.08] pt-6">
              <h4 className="text-[15px] font-semibold text-white">
                {post.comments.length === 1
                  ? "1 answer"
                  : `${post.comments.length} answers`}
              </h4>

              {post.comments.length > 0 && (
                <ul className="mt-5 space-y-5">
                  {post.comments.map((c) => (
                    <li key={c.id} className="flex gap-3">
                      <Avatar name={c.author.name} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-[13px] text-white/90">
                          <span className="font-medium text-white/90">
                            {c.author.name}
                          </span>
                          <span aria-hidden>·</span>
                          <span>{timeAgo(c.created_at)}</span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed text-white/90">
                          {c.body}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Composer */}
              <div
                className={cn("mt-6", post.comments.length === 0 && "mt-4")}
              >
                {isLoggedIn ? (
                  <div className="space-y-3">
                    <label htmlFor="fb-comment" className="sr-only">
                      Add an answer
                    </label>
                    <textarea
                      id="fb-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      maxLength={2000}
                      rows={3}
                      placeholder="Add an answer..."
                      className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.02] px-3.5 py-3 text-[14px] leading-relaxed text-white placeholder:text-white/40 transition-colors focus:border-emerald-400/50 focus:bg-white/[0.04] focus:outline-none focus:ring-1 focus:ring-emerald-400/40"
                    />
                    {error && (
                      <p className="text-[13px] text-rose-300">{error}</p>
                    )}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={submitComment}
                        disabled={submitting || !comment.trim()}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md bg-white px-4 text-[13px] font-medium text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 disabled:opacity-50"
                      >
                        {submitting && (
                          <Loader2
                            className="h-3.5 w-3.5 animate-spin"
                            aria-hidden
                          />
                        )}
                        Post answer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-4 py-3.5">
                    <p className="text-[14px] text-white/90">
                      Log in to upvote and join the discussion.
                    </p>
                    <Link
                      href="/login"
                      className="inline-flex h-8 items-center rounded-md bg-white px-4 text-[13px] font-medium text-black transition-colors hover:bg-zinc-200"
                    >
                      Log in
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── login prompt ──────────────────────────────────────────────── */

function LoginDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm border-white/10 bg-[#0c0c0e] p-6 text-white sm:rounded-xl">
        <DialogHeader className="text-left">
          <DialogTitle className="text-xl font-bold text-white">Log in to join in</DialogTitle>
          <DialogDescription className="text-[14px] text-white/90">
            Posting, upvoting, and commenting need a Xynth account. It is free to
            start.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Link
            href="/signup"
            className="h-9 rounded-md px-4 text-[13px] font-medium leading-9 text-white/90 transition-colors hover:text-white"
          >
            Create account
          </Link>
          <Link
            href="/login"
            className="inline-flex h-9 items-center rounded-md bg-white px-4 text-[13px] font-medium text-black transition-colors hover:bg-zinc-200"
          >
            Log in
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
